/**
 * Harvest Section Component
 *
 * Displays harvest (HVST) analytics with worker performance
 */

import React from 'react'
import type { TaskType, Worker } from 'src/store/todo/types'
import { formatNumber } from '../../utils'

type WorkerHarvestData = {
  worker: Worker
  totalHVSTBoxes: number
  workerPercentage: number
}

type HarvestSectionProps = {
  /** Array of HVST task types */
  hvstTasks: TaskType[]
  /** Total boxes harvested by the team */
  teamTotalBoxes: number
  /** Formatted total boxes string */
  formattedTotal: string
  /** Array of worker harvest data, sorted by performance */
  harvestWorkers: WorkerHarvestData[]
  /** Callback when worker info button is clicked - now shows ALL harvests for worker */
  onShowWorkerSummary: (workerId: string, workerName: string, harvestTaskIds: string[]) => void
}

/**
 * HarvestSection component showing harvest statistics
 *
 * @example
 * <HarvestSection
 *   hvstTasks={hvstTasks}
 *   teamTotalBoxes={150.5}
 *   formattedTotal="150.5"
 *   harvestWorkers={workerData}
 *   onShowWorkerSummary={handleShowSummary}
 * />
 */
const HarvestSection: React.FC<HarvestSectionProps> = ({
  hvstTasks,

  formattedTotal,
  harvestWorkers,
  onShowWorkerSummary,
}) => {
  if (harvestWorkers.length === 0) return null

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="font-semibold text-gray-900 mb-2">Harvest ({formattedTotal} boxes)</h3>
      <div className="space-y-1">
        {harvestWorkers.map((data) => (
          <div
            key={data.worker.id}
            className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded text-sm"
          >
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  // Pass ALL harvest task IDs to show complete tally
                  const harvestTaskIds = hvstTasks.map(t => t.id)
                  if (harvestTaskIds.length > 0) {
                    onShowWorkerSummary(
                      data.worker.id,
                      `${data.worker.firstName} ${data.worker.lastName}`,
                      harvestTaskIds
                    )
                  }
                }}
                className="w-5 h-5 rounded-md flex items-center justify-center text-xs bg-green-100 text-blue-700 hover:bg-green-200 transition-colors flex-shrink-0"
                title="Show all harvest records (all zones & locations)"
              >
                ℹ
              </button>
              <span className="font-medium text-gray-900">
                {data.worker.firstName} {data.worker.lastName}
              </span>
            </div>
            <div className="flex items-center gap-2 text-right">
              <span className="font-semibold text-gray-900">
                {formatNumber(data.totalHVSTBoxes)} boxes
              </span>
              <span className="text-xs text-gray-500">({data.workerPercentage}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default HarvestSection
